// This code is generated. To regenerate, run `cargo run --bin edge -- generate-ts-types`
import { z } from "zod";

import type { Opaque } from "@/utils/opaque";

// Defined in crates/edge/src/realtime/messages/response/evaluation.rs @ line 31
export const zWinningChances = z.object({
    white: z.number(),
    draw: z.number(),
    black: z.number(),
});
export type WinningChances = z.infer<typeof zWinningChances>;

// Defined in crates/protocol/src/entities/viewer.rs @ line 18
const zUserIdInner = z.string().uuid();
type UserIdInner = z.infer<typeof zUserIdInner>;
export type UserId = Opaque<UserIdInner, "UserId">;
export const zUserId = zUserIdInner.transform(val => val as UserId);

// Defined in crates/edge/src/realtime/messages/response/trials.rs @ line 46
export const zTrialsOrderBy = z
    .enum(["HighestRatedPlayer", "RoundStartAt", "unknown"])
    .catch("unknown");
export type TrialsOrderBy = z.infer<typeof zTrialsOrderBy>;

// Defined in crates/edge/src/realtime/messages/response/trial.rs @ line 155
export const zTrialStatus = z
    .enum(["live", "finished", "starts_soon", "upcoming", "unknown"])
    .catch("unknown");
export type TrialStatus = z.infer<typeof zTrialStatus>;

// Defined in crates/protocol/src/entities/game_hierarchy/trial.rs @ line 250
export const zTrialResult = z
    .enum(["Player1Win", "Player2Win", "Draw", "Aborted", "unknown"])
    .catch("unknown");
export type TrialResult = z.infer<typeof zTrialResult>;

// Defined in crates/edge/src/realtime/messages/response/trial.rs @ line 25
export const zTrialKind = z.enum(["Single", "Multiple", "unknown"]).catch("unknown");
export type TrialKind = z.infer<typeof zTrialKind>;

// Defined in crates/protocol/src/entities/game_hierarchy/formats.rs @ line 16
export const zTrialFormat = z.object({
    steps: z.array(
        z.object({
            point_system: z.enum(["Standard", "Armageddon", "NorwayChessClassical"]),
            time_control: z.nullable(z.string()).default(null),
            games: z.union([
                z.object({ Fixed: z.number() }),
                z.object({ RepeatUntilDecisive: z.number() }),
            ]),
        })
    ),
    points: z.enum(["Standard", "Double", "SumPoints"]),
});
export type TrialFormat = z.infer<typeof zTrialFormat>;

// Defined in crates/edge/src/features/tournament_stage/query.rs @ line 15
export const zTournamentStageKind = z
    .enum(["pool", "elimination", "unsupported", "unknown"])
    .catch("unknown");
export type TournamentStageKind = z.infer<typeof zTournamentStageKind>;

// Defined in crates/protocol/src/entities/layout/shared_blocks.rs @ line 7
export const zTournamentRoundsBlock = z.object({});
export type TournamentRoundsBlock = z.infer<typeof zTournamentRoundsBlock>;

// Defined in crates/protocol/src/entities/layout/shared_blocks.rs @ line 15
export const zTournamentHighlightButtonBlock = z.object({
    gameId: z.string().uuid(),
});
export type TournamentHighlightButtonBlock = z.infer<typeof zTournamentHighlightButtonBlock>;

// Defined in crates/protocol/src/entities/layout/shared_blocks.rs @ line 42
export const zUrlKind = z.discriminatedUnion("t", [
    z.object({
        t: z.literal("external"),
        c: z.string(),
    }),
    z.object({
        t: z.literal("internal"),
        c: z.string(),
    }),
]);
export type UrlKind = z.infer<typeof zUrlKind>;

// Defined in crates/protocol/src/chess/board.rs @ line 120
export const zSquareHighlightColor = z
    .enum(["red", "green", "blue", "yellow", "unknown"])
    .catch("unknown");
export type SquareHighlightColor = z.infer<typeof zSquareHighlightColor>;

const zSquareInner = z.string();
type SquareInner = z.infer<typeof zSquareInner>;
export type Square = Opaque<SquareInner, "Square">;
export const zSquare = zSquareInner.transform(val => val as Square);

// Defined in crates/protocol/src/chess/pieces.rs @ line 25
export const zSerializableColor = z.enum(["b", "w"]);
export type SerializableColor = z.infer<typeof zSerializableColor>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 161
export const zRowSizing = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("showAll"),
        }),
        z.object({
            t: z.literal("scrolling"),
            c: z.object({
                card_width: z.number(),
            }),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type RowSizing = z.infer<typeof zRowSizing>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 144
const zRowBlockNonrecursive = z.object({
    sizing: zRowSizing,
    titleFontSize: z.number(),
});
export type RowBlock = z.infer<typeof zRowBlockNonrecursive> & {
    blocks: z.infer<typeof zLayoutBlock>[];
};
type RowBlockInput = z.input<typeof zRowBlockNonrecursive> & {
    blocks: z.input<typeof zLayoutBlock>[];
};
export const zRowBlock: z.ZodType<RowBlock, z.ZodTypeDef, RowBlockInput> =
    zRowBlockNonrecursive.extend({
        blocks: z.lazy(() => z.array(zLayoutBlock)),
    });

// Defined in crates/protocol/src/chess/pieces.rs @ line 45
export const zRole = z.enum(["p", "n", "b", "r", "q", "k"]);
export type Role = z.infer<typeof zRole>;

// Defined in crates/protocol/src/entities/reactable/mod.rs @ line 33
const zReactableIdInner = z.string().uuid();
type ReactableIdInner = z.infer<typeof zReactableIdInner>;
export type ReactableId = Opaque<ReactableIdInner, "ReactableId">;
export const zReactableId = zReactableIdInner.transform(val => val as ReactableId);

// Defined in crates/edge/src/realtime/messages/response/poll.rs @ line 6
export const zPollOption = z.object({
    id: z.string().uuid(),
    text: z.string(),
});
export type PollOption = z.infer<typeof zPollOption>;

// Defined in crates/edge/src/realtime/messages/response/poll.rs @ line 13
export const zPoll = z.object({
    id: z.string().uuid(),
    endAt: z.string().transform(arg => new Date(arg)),
    title: z.string(),
    options: z.array(zPollOption),
});
export type Poll = z.infer<typeof zPoll>;

// Defined in crates/protocol/src/entities/ply.rs @ line 15
const zPlyIdInner = z.string().uuid();
type PlyIdInner = z.infer<typeof zPlyIdInner>;
export type PlyId = Opaque<PlyIdInner, "PlyId">;
export const zPlyId = zPlyIdInner.transform(val => val as PlyId);

// Defined in crates/ttt-tournament-calculations/src/player_stage_standing.rs @ line 14
export const zPlayerStageStanding = z.object({
    stageId: z.string().uuid(),
    points: z.number(),
    rank: z.number(),
    previousRank: z.number(),
    trialsPlayed: z.number(),
    isPlaying: z.boolean(),
});
export type PlayerStageStanding = z.infer<typeof zPlayerStageStanding>;

// Defined in crates/protocol/src/entities/game_hierarchy/player_color.rs @ line 20
export const zPlayerColor = z.enum(["black", "white"]);
export type PlayerColor = z.infer<typeof zPlayerColor>;

// Defined in crates/protocol/src/common.rs @ line 17
export const zOrderDirection = z.enum(["Asc", "Desc", "unknown"]).catch("unknown");
export type OrderDirection = z.infer<typeof zOrderDirection>;

// Defined in crates/edge/src/realtime/messages/response/trials.rs @ line 52
export const zTrialsOrderInput = z.object({
    by: zTrialsOrderBy,
    direction: zOrderDirection,
});
export type TrialsOrderInput = z.infer<typeof zTrialsOrderInput>;

// Defined in crates/protocol/src/entities/game_hierarchy/points.rs @ line 20
export const zTieBreaker = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("Buchholz"),
            c: z.object({
                cut: z.nullable(z.number()).default(null),
            }),
        }),
        z.object({
            t: z.literal("DirectEncounter"),
            c: z.object({
                onlyTwoWay: z.boolean(),
            }),
        }),
        z.object({
            t: z.literal("AverageOpponentRating"),
            c: z.object({
                cut: z.nullable(z.number()).default(null),
            }),
        }),
        z.object({
            t: z.literal("NumberOfWins"),
            c: z.object({}),
        }),
        z.object({
            t: z.literal("NumberOfWinsWithBlack"),
            c: z.object({}),
        }),
        z.object({
            t: z.literal("SonnebornBerger"),
            c: z.object({}),
        }),
        z.object({
            t: z.literal("GamesPlayedWithBlack"),
            c: z.object({}),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type TieBreaker = z.infer<typeof zTieBreaker>;

// Defined in crates/protocol/src/entities/game_hierarchy/points.rs @ line 369
export const zTournamentFormat = z.object({
    tie_breakers: z.array(zTieBreaker),
});
export type TournamentFormat = z.infer<typeof zTournamentFormat>;

// Defined in crates/edge/src/realtime/messages/response/tournament_stage.rs @ line 11
export const zTournamentStage = z.object({
    id: z.string().uuid(),
    created_at: z.string().transform(arg => new Date(arg)),
    tournament_id: z.string().uuid(),
    title: z.string(),
    kind: zTournamentStageKind,
    description: z.nullable(z.any()).default(null),
});
export type TournamentStage = z.infer<typeof zTournamentStage>;

// Defined in crates/edge/src/realtime/messages/response/streaming_channel_sessions.rs @ line 8
export const zStreamingChannelSessionsInput = z.object({
    streamingChannelIds: z.nullable(z.array(z.string().uuid())).default(null),
});
export type StreamingChannelSessionsInput = z.infer<typeof zStreamingChannelSessionsInput>;

// Defined in crates/edge/src/realtime/messages/response/tournament.rs @ line 7
export const zTournament = z.object({
    id: z.string().uuid(),
    title: z.string(),
    shortTitle: z.string(),
    heroImageId: z.nullable(z.string().uuid()).default(null),
    thumbnailImageId: z.nullable(z.string().uuid()).default(null),
    badgeImageId: z.nullable(z.string().uuid()).default(null),
});
export type Tournament = z.infer<typeof zTournament>;

// Defined in crates/protocol/src/entities/game_hierarchy/formats.rs @ line 46
export const zTrialProgress = z.object({
    result: z.nullable(zTrialResult).default(null),
    player1Score: z.number(),
    player2Score: z.number(),
    format: zTrialFormat,
});
export type TrialProgress = z.infer<typeof zTrialProgress>;

// Defined in crates/edge/src/realtime/messages/response/video.rs @ line 5
export const zVideo = z.object({
    id: z.string().uuid(),
    muxPlaybackId: z.string(),
    playbackUrl: z.string(),
    thumbnailImageId: z.nullable(z.string().uuid()).default(null),
    duration: z.nullable(z.number()).default(null),
    aspectRatio: z.nullable(z.string()).default(null),
});
export type Video = z.infer<typeof zVideo>;

// Defined in crates/edge/src/realtime/messages/response/streaming_channel_sessions.rs @ line 28
export const zRealtimeStreamingChannelSession = z.object({
    id: z.string().uuid(),
    streamingChannelId: z.string().uuid(),
    externalStreamId: z.string(),
    title: z.nullable(z.string()).default(null),
    scheduledStartTime: z.nullable(z.string().transform(arg => new Date(arg))).default(null),
    actualStartTime: z.nullable(z.string().transform(arg => new Date(arg))).default(null),
    detectedEndTime: z.nullable(z.string().transform(arg => new Date(arg))).default(null),
    externalChannelHandle: z.string(),
    externalChannelId: z.string(),
    provider: z.string(),
    channelImageId: z.nullable(z.string().uuid()).default(null),
    isLive: z.boolean(),
    isUpcoming: z.boolean(),
    streamUrl: z.string(),
});
export type RealtimeStreamingChannelSession = z.infer<typeof zRealtimeStreamingChannelSession>;

// Defined in crates/edge/src/realtime/messages/response/streaming_channel_sessions.rs @ line 47
export const zStreamingChannelSessionsData = z.object({
    sessions: z.array(zRealtimeStreamingChannelSession),
    lastUpdated: z.string().transform(arg => new Date(arg)),
});
export type StreamingChannelSessionsData = z.infer<typeof zStreamingChannelSessionsData>;

// Defined in crates/protocol/src/chess/pieces.rs @ line 12
export const zPiece = z.object({
    square: z.nullable(zSquare).default(null),
    role: zRole,
    color: zSerializableColor,
});
export type Piece = z.infer<typeof zPiece>;

// Defined in crates/protocol/src/chess/pieces.rs @ line 8
const zPiecesInner = z.array(zPiece);
type PiecesInner = z.infer<typeof zPiecesInner>;
export type Pieces = Opaque<PiecesInner, "Pieces">;
export const zPieces = zPiecesInner.transform(val => val as Pieces);

// Defined in crates/edge/src/realtime/messages/response/tournament_brackets.rs @ line 17
export const zMatchState = z
    .enum(["notStarted", "inProgress", "completed", "unknown"])
    .catch("unknown");
export type MatchState = z.infer<typeof zMatchState>;

// Defined in crates/edge/src/realtime/messages/response/machine_analysis.rs @ line 82
export const zMachineAnalysis = z.object({
    plyId: zPlyId,
    plyIndex: z.number(),
    color: zSerializableColor,
    accuracy: z.number(),
    winPercentage: z.number(),
});
export type MachineAnalysis = z.infer<typeof zMachineAnalysis>;

// Defined in crates/edge/src/realtime/messages/response/machine_analysis.rs @ line 30
export const zMachineAnalysisUpdate = z.object({
    updated: z.array(zMachineAnalysis),
});
export type MachineAnalysisUpdate = z.infer<typeof zMachineAnalysisUpdate>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 71
export const zLatestContentFilter = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("onlyWithVideo"),
        }),
        z.object({
            t: z.literal("onlyStories"),
        }),
        z.object({
            t: z.literal("all"),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type LatestContentFilter = z.infer<typeof zLatestContentFilter>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 62
export const zLatestContentBlock = z.object({
    title: z.string(),
    subtitle: z.string(),
    filter: zLatestContentFilter,
    num: z.number(),
});
export type LatestContentBlock = z.infer<typeof zLatestContentBlock>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 144
export const zLabelStyle = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("md"),
        }),
        z.object({
            t: z.literal("lg"),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type LabelStyle = z.infer<typeof zLabelStyle>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 135
export const zLabelColor = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("blue"),
        }),
        z.object({
            t: z.literal("green"),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type LabelColor = z.infer<typeof zLabelColor>;

// Defined in crates/protocol/src/entities/game_hierarchy/tournament_series.rs @ line 96
export const zStandingAggregateMethod = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("Points"),
        }),
        z.object({
            t: z.literal("Rank"),
            c: z.object({
                min_points: z.number(),
                rank_points: z.record(z.number(), z.number()),
                knockout: z.boolean(),
            }),
        }),
        z.object({
            t: z.literal("Manual"),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type StandingAggregateMethod = z.infer<typeof zStandingAggregateMethod>;

// Defined in crates/protocol/src/entities/game_hierarchy/tournament_series.rs @ line 77
export const zStandingsCalculation = z.object({
    player_rank_cap: z.nullable(z.number()).default(null),
    method: zStandingAggregateMethod,
});
export type StandingsCalculation = z.infer<typeof zStandingsCalculation>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 260
export const zGuessTheMoveLeaderboardEntry = z.object({
    rank: z.number(),
    displayName: z.string(),
    points: z.number(),
    gameId: z.string().uuid(),
});
export type GuessTheMoveLeaderboardEntry = z.infer<typeof zGuessTheMoveLeaderboardEntry>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 254
export const zGuessTheMoveLeaderboardBlock = z.object({
    entries: z.array(zGuessTheMoveLeaderboardEntry),
});
export type GuessTheMoveLeaderboardBlock = z.infer<typeof zGuessTheMoveLeaderboardBlock>;

// Defined in crates/edge/src/realtime/messages/response/guess.rs @ line 72
export const zGuessResolution = z.object({
    points: z.number(),
    resolvedAt: z.string().transform(arg => new Date(arg)),
});
export type GuessResolution = z.infer<typeof zGuessResolution>;

// Defined in crates/edge/src/realtime/messages/response/games_metadata.rs @ line 75
export const zGamesMetadataOrderBy = z
    .enum(["TotalRating", "RoundStartAt", "GameStartedAt", "PositionInTrial", "unknown"])
    .catch("unknown");
export type GamesMetadataOrderBy = z.infer<typeof zGamesMetadataOrderBy>;

// Defined in crates/edge/src/realtime/messages/response/games_metadata.rs @ line 83
export const zGamesMetadataOrderInput = z.object({
    by: zGamesMetadataOrderBy,
    direction: zOrderDirection,
});
export type GamesMetadataOrderInput = z.infer<typeof zGamesMetadataOrderInput>;

// Defined in crates/edge/src/realtime/messages/response/game/game_status.rs @ line 11
export const zGameStatus = z
    .enum(["live", "finished", "forfeited", "starts_soon", "upcoming", "unknown"])
    .catch("unknown");
export type GameStatus = z.infer<typeof zGameStatus>;

// Defined in crates/protocol/src/entities/game_hierarchy/game.rs @ line 182
export const zGameResult = z
    .enum(["WHITE", "BLACK", "DRAW", "ABORTED", "unknown"])
    .catch("unknown");
export type GameResult = z.infer<typeof zGameResult>;

// Defined in crates/edge/src/realtime/messages/response/evaluation.rs @ line 21
export const zScalarScore = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("cp"),
            v: z.number(),
        }),
        z.object({
            t: z.literal("mate"),
            v: z.number(),
        }),
        z.object({
            t: z.literal("decisive"),
            v: zGameResult,
        }),
        z.object({
            t: z.literal("unknown"),
            v: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type ScalarScore = z.infer<typeof zScalarScore>;

// Defined in crates/protocol/src/entities/game_hierarchy/game.rs @ line 32
const zGameIdInner = z.string().uuid();
type GameIdInner = z.infer<typeof zGameIdInner>;
export type GameId = Opaque<GameIdInner, "GameId">;
export const zGameId = zGameIdInner.transform(val => val as GameId);

// Defined in crates/edge/src/realtime/messages/response/followed_players_data.rs @ line 17
export const zFollowedPlayersDataOptionsInput = z.object({
    maxOneGamePerPlayer: z.boolean(),
});
export type FollowedPlayersDataOptionsInput = z.infer<typeof zFollowedPlayersDataOptionsInput>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 65
export const zFideTitleCount = z.object({
    title: z.string(),
    count: z.number(),
});
export type FideTitleCount = z.infer<typeof zFideTitleCount>;

// Defined in crates/edge/src/realtime/messages/response/feed.rs @ line 117
export const zFeedRecordCommentator = z.object({
    imageId: z.string().uuid(),
    displayName: z.string(),
});
export type FeedRecordCommentator = z.infer<typeof zFeedRecordCommentator>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 269
export const zFeaturedStreamingChannelBlock = z.object({
    streamingChannelId: z.nullable(z.string().uuid()).default(null),
    title: z.nullable(z.string()).default(null),
    backgroundImageId: z.nullable(z.string().uuid()).default(null),
});
export type FeaturedStreamingChannelBlock = z.infer<typeof zFeaturedStreamingChannelBlock>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 126
export const zErrorBlock = z.object({
    error: z.string(),
});
export type ErrorBlock = z.infer<typeof zErrorBlock>;

// Defined in crates/protocol/src/entities/game_hierarchy/elimination_settings.rs @ line 102
export const zEliminationFormat = z.enum(["single", "double", "unknown"]).catch("unknown");
export type EliminationFormat = z.infer<typeof zEliminationFormat>;

// Defined in crates/protocol/src/common.rs @ line 35
export const zDateRangeInput = z.object({
    gte: z.string().transform(arg => new Date(arg)),
    lt: z.string().transform(arg => new Date(arg)),
});
export type DateRangeInput = z.infer<typeof zDateRangeInput>;

// Defined in crates/edge/src/realtime/messages/response/trials.rs @ line 8
export const zTrialsFilterInput = z.object({
    dateRange: z.nullable(zDateRangeInput).default(null),
    withPlayerId: z.nullable(z.string().uuid()).default(null),
    withRoundId: z.nullable(z.string().uuid()).default(null),
});
export type TrialsFilterInput = z.infer<typeof zTrialsFilterInput>;

// Defined in crates/edge/src/realtime/messages/response/trials.rs @ line 105
export const zTrialsInput = z.object({
    filter: zTrialsFilterInput,
    order: z.nullable(z.array(zTrialsOrderInput)).default(null),
});
export type TrialsInput = z.infer<typeof zTrialsInput>;

// Defined in crates/edge/src/realtime/messages/response/games_metadata.rs @ line 11
export const zGamesMetadataFilterInput = z
    .object({
        date_range: z.nullable(zDateRangeInput).default(null),
        only_live: z.nullable(z.boolean()).default(null),
        player_id: z.nullable(z.string().uuid()).default(null),
        trial_id: z.nullable(z.string().uuid()).default(null),
        with_tournament_id: z.nullable(z.string().uuid()).default(null),
    })
    .passthrough();
export type GamesMetadataFilterInput = z.infer<typeof zGamesMetadataFilterInput>;

// Defined in crates/edge/src/realtime/messages/response/games_metadata.rs @ line 89
export const zGamesMetadataInput = z.object({
    filter: zGamesMetadataFilterInput,
    order: z.nullable(z.array(zGamesMetadataOrderInput)).default(null),
    limit: z.nullable(z.number()).default(null),
    offset: z.nullable(z.number()).default(null),
});
export type GamesMetadataInput = z.infer<typeof zGamesMetadataInput>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 169
export const zGamesBlock = z.object({
    filter: zGamesMetadataFilterInput,
});
export type GamesBlock = z.infer<typeof zGamesBlock>;

// Defined in crates/edge/src/realtime/messages/response/followed_players_data.rs @ line 23
export const zFollowedPlayersDataFilterInput = z.object({
    dateRange: zDateRangeInput,
    followedPlayerIds: z.array(z.string().uuid()),
});
export type FollowedPlayersDataFilterInput = z.infer<typeof zFollowedPlayersDataFilterInput>;

// Defined in crates/edge/src/realtime/messages/response/followed_players_data.rs @ line 30
export const zFollowedPlayersDataInput = z.object({
    filter: zFollowedPlayersDataFilterInput,
    options: zFollowedPlayersDataOptionsInput,
});
export type FollowedPlayersDataInput = z.infer<typeof zFollowedPlayersDataInput>;

// Defined in crates/protocol/src/entities/player.rs @ line 40
const zCountryCodeInner = z.string();
type CountryCodeInner = z.infer<typeof zCountryCodeInner>;
export type CountryCode = Opaque<CountryCodeInner, "CountryCode">;
export const zCountryCode = zCountryCodeInner.transform(val => val as CountryCode);

// Defined in crates/edge/src/realtime/messages/response/player.rs @ line 6
export const zPlayer = z.object({
    id: z.string().uuid(),
    nickname: z.string(),
    name: z.string(),
    countryCode: z.nullable(zCountryCode).default(null),
    fideTitle: z.nullable(z.string()).default(null),
    fideRatingStandard: z.nullable(z.number()).default(null),
    imageId: z.nullable(z.string().uuid()).default(null),
});
export type Player = z.infer<typeof zPlayer>;

// Defined in crates/edge/src/realtime/messages/response/tournament_players.rs @ line 9
export const zTournamentPlayer = z.object({
    player: zPlayer,
    stageStandings: z.array(zPlayerStageStanding),
});
export type TournamentPlayer = z.infer<typeof zTournamentPlayer>;

// Defined in crates/edge/src/realtime/messages/response/tournament_players.rs @ line 23
export const zTournamentPlayersData = z.object({
    players: z.array(zTournamentPlayer),
    lastUpdated: z.nullable(z.string().transform(arg => new Date(arg))).default(null),
});
export type TournamentPlayersData = z.infer<typeof zTournamentPlayersData>;

// Defined in crates/edge/src/realtime/messages/response/tournament_players.rs @ line 16
export const zTournamentPlayers = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("Updated"),
            c: zTournamentPlayersData,
        }),
        z.object({
            t: z.literal("Removed"),
            c: z.string().uuid(),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type TournamentPlayers = z.infer<typeof zTournamentPlayers>;

// Defined in crates/edge/src/realtime/messages/response/tournament_standings.rs @ line 16
export const zStandingsPlayer = z.object({
    id: z.string().uuid(),
    player: zPlayer,
    previousPoints: z.number(),
    points: z.number(),
    rank: z.number(),
    previousRank: z.number(),
    gamesPlayed: z.number(),
    isPlaying: z.boolean(),
});
export type StandingsPlayer = z.infer<typeof zStandingsPlayer>;

// Defined in crates/edge/src/realtime/messages/response/tournament_standings.rs @ line 7
export const zRealtimeTournamentStandings = z.object({
    tournamentId: z.string().uuid(),
    tournamentTitle: z.string(),
    items: z.array(zStandingsPlayer),
    totalRounds: z.number(),
});
export type RealtimeTournamentStandings = z.infer<typeof zRealtimeTournamentStandings>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 93
export const zPodiumPlayer = z.object({
    rank: z.number(),
    player: zPlayer,
    points: z.number(),
});
export type PodiumPlayer = z.infer<typeof zPodiumPlayer>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 85
export const zTournamentPodiumBlock = z.object({
    title: z.nullable(z.string()).default(null),
    otherTournamentId: z.nullable(z.string().uuid()).default(null),
    podium: z.array(zPodiumPlayer),
});
export type TournamentPodiumBlock = z.infer<typeof zTournamentPodiumBlock>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 72
export const zTournamentPlayersButtonBlock = z.object({
    highlights: z.array(zPlayer),
    playerCount: z.number(),
    fideTitles: z.array(zFideTitleCount),
    highestRated: z.nullable(zPlayer).default(null),
});
export type TournamentPlayersButtonBlock = z.infer<typeof zTournamentPlayersButtonBlock>;

// Defined in crates/edge/src/realtime/messages/response/commentator.rs @ line 9
export const zLocalizedCommentator = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    imageId: z.string().uuid(),
    player: z.nullable(zPlayer).default(null),
    countryCode: zCountryCode,
    shortDescription: z.nullable(z.string()).default(null),
});
export type LocalizedCommentator = z.infer<typeof zLocalizedCommentator>;

// Defined in crates/edge/src/realtime/messages/response/game/game_report_data.rs @ line 221
export const zGameReportMeta = z.object({
    gameId: zGameId,
    languageCode: z.string(),
    pieceOfShit: z.nullable(z.string()).default(null),
    pieceOfShitDescription: z.nullable(z.string()).default(null),
    mostValuablePiece: z.nullable(z.string()).default(null),
    mostValuablePieceDescription: z.nullable(z.string()).default(null),
    commentator: z.nullable(zLocalizedCommentator).default(null),
    pregamePoll: z.nullable(zPoll).default(null),
    recapVideo: z.nullable(zVideo).default(null),
});
export type GameReportMeta = z.infer<typeof zGameReportMeta>;

// Defined in crates/edge/src/realtime/messages/response/game/game_metadata.rs @ line 76
export const zGameMetadataGameReportInfo = z.object({
    commentator: z.nullable(zLocalizedCommentator).default(null),
    languageCode: z.string(),
});
export type GameMetadataGameReportInfo = z.infer<typeof zGameMetadataGameReportInfo>;

// Defined in crates/edge/src/realtime/messages/response/game/game_metadata.rs @ line 32
export const zGameMetadata = z.object({
    id: zGameId,
    whitePlayer: zPlayer,
    blackPlayer: zPlayer,
    trialPlayer1Id: z.string().uuid(),
    trialPlayer2Id: z.string().uuid(),
    scheduledStartAt: z.string().transform(arg => new Date(arg)),
    roundStartAt: z.string().transform(arg => new Date(arg)),
    result: z.nullable(zGameResult).default(null),
    isLive: z.boolean(),
    tournamentId: z.string().uuid(),
    roundTournamentStageId: z.string().uuid(),
    roundTournamentStageKind: zTournamentStageKind,
    contextLabel: z.nullable(z.string()).default(null),
    status: zGameStatus,
    isStandalone: z.boolean(),
    gameReports: z.array(zGameMetadataGameReportInfo),
    trialProgress: zTrialProgress,
    indexInTrial: z.number(),
    currentTotalGames: z.nullable(z.number()).default(null),
    trialId: z.string().uuid(),
    trialStatus: zTrialStatus,
    trialKind: zTrialKind,
    tournamentStage: zTournamentStage,
    tournament: zTournament,
    roundTitle: z.string(),
    termination: z.nullable(z.string()).default(null),
    startedAt: z.nullable(z.string().transform(arg => new Date(arg))).default(null),
    positionInTrial: z.nullable(z.number()).default(null),
});
export type GameMetadata = z.infer<typeof zGameMetadata>;

// Defined in crates/edge/src/realtime/messages/response/games_metadata.rs @ line 141
export const zGamesMetadata = z.object({
    items: z.array(zGameMetadata),
    total: z.number(),
    hasMore: z.boolean(),
    after: z.nullable(z.string().uuid()).default(null),
});
export type GamesMetadata = z.infer<typeof zGamesMetadata>;

// Defined in crates/edge/src/realtime/messages/response/game/game.rs @ line 31
export const zGameGameReport = z.object({
    id: z.string().uuid(),
    commentator: z.nullable(zLocalizedCommentator).default(null),
    languageCode: z.string(),
});
export type GameGameReport = z.infer<typeof zGameGameReport>;

// Defined in crates/edge/src/realtime/messages/response/game/game.rs @ line 17
export const zGame = z.object({
    id: zGameId,
    player1Color: zPlayerColor,
    player2Color: zPlayerColor,
    startedAt: z.nullable(z.string().transform(arg => new Date(arg))).default(null),
    scheduledStartAt: z.string().transform(arg => new Date(arg)),
    gameStatus: zGameStatus,
    result: z.nullable(zGameResult).default(null),
    gameReports: z.array(zGameGameReport),
    positionInTrial: z.nullable(z.number()).default(null),
});
export type Game = z.infer<typeof zGame>;

// Defined in crates/edge/src/realtime/messages/response/trial.rs @ line 31
export const zTrial = z.object({
    id: z.string().uuid(),
    label: z.string(),
    player1: zPlayer,
    player2: zPlayer,
    status: zTrialStatus,
    progress: zTrialProgress,
    roundStartAt: z.string().transform(arg => new Date(arg)),
    roundTitle: z.string(),
    activeGame: z.nullable(zGame).default(null),
    kind: zTrialKind,
    tournamentStage: zTournamentStage,
    tournament: zTournament,
    format: zTrialFormat,
});
export type Trial = z.infer<typeof zTrial>;

// Defined in crates/edge/src/realtime/messages/response/trials.rs @ line 170
export const zTrials = z.object({
    items: z.array(zTrial),
    total: z.number(),
    hasMore: z.boolean(),
    after: z.nullable(z.string().uuid()).default(null),
});
export type Trials = z.infer<typeof zTrials>;

// Defined in crates/edge/src/realtime/messages/response/followed_players_data.rs @ line 82
export const zFollowedPlayersDataV2 = z.object({
    games: z.array(zGameMetadata),
});
export type FollowedPlayersDataV2 = z.infer<typeof zFollowedPlayersDataV2>;

// Defined in crates/edge/src/realtime/messages/response/followed_players_data.rs @ line 76
export const zFollowedPlayersData = z.object({
    gamesByTournamentId: z.record(z.string().uuid(), z.array(zGameMetadata)),
});
export type FollowedPlayersData = z.infer<typeof zFollowedPlayersData>;

// Defined in crates/edge/src/realtime/messages/response/country_standings.rs @ line 33
export const zContributingPlayer = z.object({
    playerId: z.string().uuid(),
    playerName: z.string(),
    points: z.number(),
    rankInCountry: z.number(),
});
export type ContributingPlayer = z.infer<typeof zContributingPlayer>;

// Defined in crates/edge/src/realtime/messages/response/country_standings.rs @ line 23
export const zCountryStanding = z.object({
    countryCode: z.string(),
    totalPoints: z.number(),
    rank: z.number(),
    playerCount: z.number(),
    contributingPlayers: z.array(zContributingPlayer),
});
export type CountryStanding = z.infer<typeof zCountryStanding>;

// Defined in crates/edge/src/realtime/messages/response/country_standings.rs @ line 14
export const zRealtimeCountryStandings = z.object({
    tournamentId: z.string().uuid(),
    tournamentTitle: z.string(),
    items: z.array(zCountryStanding),
    topPlayersPerCountry: z.nullable(z.number()).default(null),
});
export type RealtimeCountryStandings = z.infer<typeof zRealtimeCountryStandings>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 132
const zContainerBlockNonrecursive = z.object({
    tournamentId: z.nullable(z.string().uuid()).default(null),
    stickyTitle: z.nullable(z.string()).default(null),
    transparent: z.boolean(),
});
export type ContainerBlock = z.infer<typeof zContainerBlockNonrecursive> & {
    blocks: z.infer<typeof zLayoutBlock>[];
};
type ContainerBlockInput = z.input<typeof zContainerBlockNonrecursive> & {
    blocks: z.input<typeof zLayoutBlock>[];
};
export const zContainerBlock: z.ZodType<ContainerBlock, z.ZodTypeDef, ContainerBlockInput> =
    zContainerBlockNonrecursive.extend({
        blocks: z.lazy(() => z.array(zLayoutBlock)),
    });

// Defined in crates/edge/src/realtime/messages/response/guess.rs @ line 147
export const zConcreteMove = z.object({
    from: zSquare,
    to: zSquare,
    promotion: z.nullable(zRole).default(null),
});
export type ConcreteMove = z.infer<typeof zConcreteMove>;

// Defined in crates/edge/src/realtime/messages/response/guess.rs @ line 140
export const zGuessedMove = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("Exact"),
            c: zConcreteMove,
        }),
        z.object({
            t: z.literal("Exclusion"),
            c: z.array(zConcreteMove),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type GuessedMove = z.infer<typeof zGuessedMove>;

// Defined in crates/edge/src/realtime/messages/response/guess.rs @ line 38
export const zGuessOptions = z.object({
    gameId: zGameId,
    atPlyId: zPlyId,
    options: z.array(zConcreteMove),
});
export type GuessOptions = z.infer<typeof zGuessOptions>;

// Defined in crates/edge/src/realtime/messages/response/guess.rs @ line 46
export const zGuessAggregateAnswer = z.object({
    move: zGuessedMove,
    guesses: z.number(),
});
export type GuessAggregateAnswer = z.infer<typeof zGuessAggregateAnswer>;

// Defined in crates/edge/src/realtime/messages/response/guess.rs @ line 53
export const zGuessAggregate = z.object({
    gameId: zGameId,
    atPlyId: zPlyId,
    answerDistribution: z.array(zGuessAggregateAnswer),
});
export type GuessAggregate = z.infer<typeof zGuessAggregate>;

// Defined in crates/edge/src/realtime/messages/response/guess.rs @ line 61
export const zGuess = z.object({
    id: z.string().uuid(),
    gameId: zGameId,
    userId: zUserId,
    atPlyId: zPlyId,
    move: zGuessedMove,
    resolution: z.nullable(zGuessResolution).default(null),
});
export type Guess = z.infer<typeof zGuess>;

// Defined in crates/edge/src/realtime/messages/response/guess.rs @ line 27
export const zGuessRun = z.object({
    id: z.string().uuid(),
    gameId: zGameId,
    color: zPlayerColor,
    startedAt: z.string().transform(arg => new Date(arg)),
    completedAt: z.nullable(z.string().transform(arg => new Date(arg))).default(null),
    guesses: z.array(zGuess),
});
export type GuessRun = z.infer<typeof zGuessRun>;

// Defined in crates/edge/src/realtime/messages/response/guess.rs @ line 10
export const zGuessedMoveUpdate = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("Init"),
            c: z.object({
                maxGuessesPerRun: z.number(),
                guessRuns: z.array(zGuessRun),
            }),
        }),
        z.object({
            t: z.literal("OptionsReady"),
            c: zGuessOptions,
        }),
        z.object({
            t: z.literal("Update"),
            c: zGuess,
        }),
        z.object({
            t: z.literal("Confirmation"),
            c: z.object({
                atPlyId: zPlyId,
                potentialPoints: z.number(),
            }),
        }),
        z.object({
            t: z.literal("Aggregate"),
            c: zGuessAggregate,
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type GuessedMoveUpdate = z.infer<typeof zGuessedMoveUpdate>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 153
const zColumnBlockNonrecursive = z.object({
    titleFontSize: z.number(),
});
export type ColumnBlock = z.infer<typeof zColumnBlockNonrecursive> & {
    blocks: z.infer<typeof zLayoutBlock>[];
};
type ColumnBlockInput = z.input<typeof zColumnBlockNonrecursive> & {
    blocks: z.input<typeof zLayoutBlock>[];
};
export const zColumnBlock: z.ZodType<ColumnBlock, z.ZodTypeDef, ColumnBlockInput> =
    zColumnBlockNonrecursive.extend({
        blocks: z.lazy(() => z.array(zLayoutBlock)),
    });

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 100
export const zCmsTournamentRoundsBlock = z.object({});
export type CmsTournamentRoundsBlock = z.infer<typeof zCmsTournamentRoundsBlock>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 110
export const zCmsTournamentPodiumBlock = z.object({
    otherTournamentId: z.nullable(z.string().uuid()).default(null),
    title: z.nullable(z.string()).default(null),
});
export type CmsTournamentPodiumBlock = z.infer<typeof zCmsTournamentPodiumBlock>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 104
export const zCmsTournamentHighlightButton = z.object({
    gameId: z.string().uuid(),
});
export type CmsTournamentHighlightButton = z.infer<typeof zCmsTournamentHighlightButton>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 96
export const zCmsTournamentGamesBlock = z.object({});
export type CmsTournamentGamesBlock = z.infer<typeof zCmsTournamentGamesBlock>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 162
export const zCmsTeaserPresentation = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("default"),
        }),
        z.object({
            t: z.literal("defaultWide"),
        }),
        z.object({
            t: z.literal("posterLg"),
        }),
        z.object({
            t: z.literal("posterMd"),
        }),
        z.object({
            t: z.literal("original"),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type CmsTeaserPresentation = z.infer<typeof zCmsTeaserPresentation>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 231
export const zCmsRowScrollingSize = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("sm"),
        }),
        z.object({
            t: z.literal("md"),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type CmsRowScrollingSize = z.infer<typeof zCmsRowScrollingSize>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 219
export const zCmsRowSizing = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("showAll"),
        }),
        z.object({
            t: z.literal("scrolling"),
            c: z.object({
                size: zCmsRowScrollingSize,
            }),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type CmsRowSizing = z.infer<typeof zCmsRowSizing>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 203
const zCmsRowBlockNonrecursive = z.object({
    sizing: zCmsRowSizing,
});
export type CmsRowBlock = z.infer<typeof zCmsRowBlockNonrecursive> & {
    blocks: string[];
};
type CmsRowBlockInput = z.input<typeof zCmsRowBlockNonrecursive> & {
    blocks: string[];
};
export const zCmsRowBlock: z.ZodType<CmsRowBlock, z.ZodTypeDef, CmsRowBlockInput> =
    zCmsRowBlockNonrecursive.extend({
        blocks: z.lazy(() => z.array(z.string().uuid())),
    });

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 54
export const zCmsFeaturedStreamingChannelBlock = z.object({
    streamingChannelId: z.nullable(z.string().uuid()).default(null),
    title: z.nullable(z.string()).default(null),
    backgroundImageId: z.nullable(z.string().uuid()).default(null),
});
export type CmsFeaturedStreamingChannelBlock = z.infer<typeof zCmsFeaturedStreamingChannelBlock>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 256
export const zCmsContainerKind = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("noLink"),
        }),
        z.object({
            t: z.literal("tournament"),
            c: z.nullable(z.string().uuid()).default(null),
        }),
        z.object({
            t: z.literal("other"),
            c: z.nullable(z.string()).default(null),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type CmsContainerKind = z.infer<typeof zCmsContainerKind>;

// Defined in crates/protocol/src/entities/layout/shared_blocks.rs @ line 21
export const zContainerTitleBlock = z.object({
    url: z.nullable(zUrlKind).default(null),
    title: z.string(),
    subtitle: z.string(),
    containerKind: zCmsContainerKind,
});
export type ContainerTitleBlock = z.infer<typeof zContainerTitleBlock>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 191
const zCmsContainerBlockNonrecursive = z.object({
    title: z.string(),
    subtitle: z.string(),
    kind: zCmsContainerKind,
    transparent: z.boolean(),
});
export type CmsContainerBlock = z.infer<typeof zCmsContainerBlockNonrecursive> & {
    blocks: string[];
};
type CmsContainerBlockInput = z.input<typeof zCmsContainerBlockNonrecursive> & {
    blocks: string[];
};
export const zCmsContainerBlock: z.ZodType<
    CmsContainerBlock,
    z.ZodTypeDef,
    CmsContainerBlockInput
> = zCmsContainerBlockNonrecursive.extend({
    blocks: z.lazy(() => z.array(z.string().uuid())),
});

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 212
const zCmsColumnBlockNonrecursive = z.object({});
export type CmsColumnBlock = z.infer<typeof zCmsColumnBlockNonrecursive> & {
    blocks: string[];
};
type CmsColumnBlockInput = z.input<typeof zCmsColumnBlockNonrecursive> & {
    blocks: string[];
};
export const zCmsColumnBlock: z.ZodType<CmsColumnBlock, z.ZodTypeDef, CmsColumnBlockInput> =
    zCmsColumnBlockNonrecursive.extend({
        blocks: z.lazy(() => z.array(z.string().uuid())),
    });

// Defined in crates/edge/src/realtime/channel.rs @ line 15
export const zChannel = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("GameUpdated"),
            c: zGameId,
        }),
        z.object({
            t: z.literal("GameMetadataUpdated"),
            c: zGameId,
        }),
        z.object({
            t: z.literal("GamePliesUpdated"),
            c: zGameId,
        }),
        z.object({
            t: z.literal("MachineAnalysesUpdated"),
            c: zGameId,
        }),
        z.object({
            t: z.literal("GameReportUpdated"),
            c: z.object({
                gameId: zGameId,
                languageCode: z.string(),
            }),
        }),
        z.object({
            t: z.literal("MultiPvPlyEvaluationUpdated"),
            c: zGameId,
        }),
        z.object({
            t: z.literal("PlyEvaluationUpdated"),
            c: zGameId,
        }),
        z.object({
            t: z.literal("LiveEvaluation"),
            c: zGameId,
        }),
        z.object({
            t: z.literal("CurrentGameChanged"),
        }),
        z.object({
            t: z.literal("TournamentStandingsUpdated"),
            c: z.string().uuid(),
        }),
        z.object({
            t: z.literal("TournamentBracketsUpdated"),
            c: z.string().uuid(),
        }),
        z.object({
            t: z.literal("TournamentPlayers"),
            c: z.string().uuid(),
        }),
        z.object({
            t: z.literal("CountryStandingsUpdated"),
            c: z.string().uuid(),
        }),
        z.object({
            t: z.literal("GuessedMovesUpdated"),
            c: z.object({
                userId: zUserId,
                gameId: zGameId,
            }),
        }),
        z.object({
            t: z.literal("FollowedPlayersDataUpdated"),
            c: zFollowedPlayersDataInput,
        }),
        z.object({
            t: z.literal("GamesMetadataUpdated"),
            c: zGamesMetadataInput,
        }),
        z.object({
            t: z.literal("TrialUpdated"),
            c: z.string().uuid(),
        }),
        z.object({
            t: z.literal("Trials"),
            c: zTrialsInput,
        }),
        z.object({
            t: z.literal("StreamingChannelSessionsUpdated"),
            c: zStreamingChannelSessionsInput,
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type Channel = z.infer<typeof zChannel>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 119
export const zButtonGroupButton = z.object({
    label: z.string(),
    url: zUrlKind,
});
export type ButtonGroupButton = z.infer<typeof zButtonGroupButton>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 107
export const zButtonGroupRow = z.object({
    buttons: z.array(zButtonGroupButton),
});
export type ButtonGroupRow = z.infer<typeof zButtonGroupRow>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 101
export const zButtonGroupBlock = z.object({
    rows: z.array(zButtonGroupRow),
});
export type ButtonGroupBlock = z.infer<typeof zButtonGroupBlock>;

// Defined in crates/edge/src/realtime/messages/response/tournament_brackets.rs @ line 44
export const zBracketPlayer = z.object({
    playerId: z.string().uuid(),
    nameTag: z.string(),
    nickname: z.string(),
    imageId: z.nullable(z.string().uuid()).default(null),
});
export type BracketPlayer = z.infer<typeof zBracketPlayer>;

// Defined in crates/edge/src/realtime/messages/response/tournament_brackets.rs @ line 25
export const zBracketMatch = z.object({
    id: z.string(),
    bracketRound: z.number(),
    positionInRound: z.number(),
    player1: z.nullable(zBracketPlayer).default(null),
    player2: z.nullable(zBracketPlayer).default(null),
    winner: z.nullable(z.string().uuid()).default(null),
    trialId: z.nullable(z.string().uuid()).default(null),
    player1Score: z.nullable(z.number()).default(null),
    player2Score: z.nullable(z.number()).default(null),
    scheduledStartAt: z.nullable(z.string().transform(arg => new Date(arg))).default(null),
    nextMatchId: z.nullable(z.string()).default(null),
    isBronzeFinal: z.boolean(),
    isGrandFinal: z.boolean(),
    state: zMatchState,
});
export type BracketMatch = z.infer<typeof zBracketMatch>;

// Defined in crates/edge/src/realtime/messages/response/tournament_brackets.rs @ line 60
export const zStageBracket = z.object({
    stageId: z.string().uuid(),
    stageTitle: z.string(),
    eliminationFormat: zEliminationFormat,
    matches: z.array(zBracketMatch),
    totalRounds: z.number(),
    includeThirdPlaceMatch: z.boolean(),
    isBracketValid: z.boolean(),
    bracketValidationMessage: z.nullable(z.string()).default(null),
});
export type StageBracket = z.infer<typeof zStageBracket>;

// Defined in crates/edge/src/realtime/messages/response/tournament_brackets.rs @ line 53
export const zRealtimeTournamentBrackets = z.object({
    tournamentId: z.string().uuid(),
    stages: z.array(zStageBracket),
});
export type RealtimeTournamentBrackets = z.infer<typeof zRealtimeTournamentBrackets>;

// Defined in crates/protocol/src/entities/article.rs @ line 147
const zArticleIdInner = z.string().uuid();
type ArticleIdInner = z.infer<typeof zArticleIdInner>;
export type ArticleId = Opaque<ArticleIdInner, "ArticleId">;
export const zArticleId = zArticleIdInner.transform(val => val as ArticleId);

// Defined in crates/edge/src/features/layout/blocks.rs @ line 177
export const zTeaserBlock = z.object({
    articleId: z.nullable(zArticleId).default(null),
    imageId: z.nullable(z.string().uuid()).default(null),
    title: z.string(),
    leadText: z.nullable(z.string()).default(null),
    label: z.nullable(z.string()).default(null),
    labelColor: zLabelColor,
    labelStyle: zLabelStyle,
    video: z.nullable(zVideo).default(null),
    url: zUrlKind,
    reactableId: z.nullable(zReactableId).default(null),
    aspectRatio: z.number(),
});
export type TeaserBlock = z.infer<typeof zTeaserBlock>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 153
export const zCmsTeaserKind = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("article"),
            c: z.nullable(zArticleId).default(null),
        }),
        z.object({
            t: z.literal("tournament"),
            c: z.nullable(z.string().uuid()).default(null),
        }),
        z.object({
            t: z.literal("other"),
            c: z.nullable(z.string()).default(null),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type CmsTeaserKind = z.infer<typeof zCmsTeaserKind>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 117
export const zCmsTeaserBlock = z.object({
    teaserKind: zCmsTeaserKind,
    imageId: z.nullable(z.string().uuid()).default(null),
    reactableId: z.nullable(zReactableId).default(null),
    title: z.string(),
    leadText: z.nullable(z.string()).default(null),
    label: z.nullable(z.string()).default(null),
    labelColor: zLabelColor,
    labelStyle: zLabelStyle,
    presentation: zCmsTeaserPresentation,
});
export type CmsTeaserBlock = z.infer<typeof zCmsTeaserBlock>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 27
export const zCmsBlockKind = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("GamesFromYourFavorites"),
        }),
        z.object({
            t: z.literal("Container"),
            c: zCmsContainerBlock,
        }),
        z.object({
            t: z.literal("Row"),
            c: zCmsRowBlock,
        }),
        z.object({
            t: z.literal("Column"),
            c: zCmsColumnBlock,
        }),
        z.object({
            t: z.literal("TournamentGames"),
            c: zCmsTournamentGamesBlock,
        }),
        z.object({
            t: z.literal("TournamentRounds"),
            c: zCmsTournamentRoundsBlock,
        }),
        z.object({
            t: z.literal("TournamentGamesButton"),
        }),
        z.object({
            t: z.literal("TournamentHighlightButton"),
            c: zCmsTournamentHighlightButton,
        }),
        z.object({
            t: z.literal("TournamentPlayersButton"),
        }),
        z.object({
            t: z.literal("TournamentStandingsButton"),
        }),
        z.object({
            t: z.literal("TournamentPodium"),
            c: zCmsTournamentPodiumBlock,
        }),
        z.object({
            t: z.literal("FeaturedStreamingChannel"),
            c: zCmsFeaturedStreamingChannelBlock,
        }),
        z.object({
            t: z.literal("Teaser"),
            c: zCmsTeaserBlock,
        }),
        z.object({
            t: z.literal("WorldRanking"),
        }),
        z.object({
            t: z.literal("LatestContent"),
            c: zLatestContentBlock,
        }),
        z.object({
            t: z.literal("AllLiveGamesButton"),
        }),
        z.object({
            t: z.literal("AllGamesButton"),
        }),
        z.object({
            t: z.literal("GuessTheMoveLeaderboard"),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type CmsBlockKind = z.infer<typeof zCmsBlockKind>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 18
export const zCmsBlock = z.object({
    id: z.string().uuid(),
    kind: zCmsBlockKind,
    featureFlagId: z.nullable(z.string()).default(null),
});
export type CmsBlock = z.infer<typeof zCmsBlock>;

// Defined in crates/protocol/src/entities/layout/cms_blocks.rs @ line 11
export const zCmsLayout = z.object({
    blocks: z.record(z.string().uuid(), zCmsBlock),
    order: z.array(z.string().uuid()),
});
export type CmsLayout = z.infer<typeof zCmsLayout>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 36
export const zBlockKind = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("GamesFromYourFavorites"),
        }),
        z.object({
            t: z.literal("Container"),
            c: zContainerBlock,
        }),
        z.object({
            t: z.literal("Row"),
            c: zRowBlock,
        }),
        z.object({
            t: z.literal("Column"),
            c: zColumnBlock,
        }),
        z.object({
            t: z.literal("ContainerTitle"),
            c: zContainerTitleBlock,
        }),
        z.object({
            t: z.literal("Games"),
            c: zGamesBlock,
        }),
        z.object({
            t: z.literal("Teaser"),
            c: zTeaserBlock,
        }),
        z.object({
            t: z.literal("TournamentRounds"),
            c: zTournamentRoundsBlock,
        }),
        z.object({
            t: z.literal("TournamentGamesButton"),
        }),
        z.object({
            t: z.literal("TournamentHighlightButton"),
            c: zTournamentHighlightButtonBlock,
        }),
        z.object({
            t: z.literal("TournamentPlayersButton"),
            c: zTournamentPlayersButtonBlock,
        }),
        z.object({
            t: z.literal("TournamentStandingsButton"),
        }),
        z.object({
            t: z.literal("TournamentPodium"),
            c: zTournamentPodiumBlock,
        }),
        z.object({
            t: z.literal("WorldRanking"),
        }),
        z.object({
            t: z.literal("ButtonGroup"),
            c: zButtonGroupBlock,
        }),
        z.object({
            t: z.literal("ErrorBlock"),
            c: zErrorBlock,
        }),
        z.object({
            t: z.literal("GuessTheMoveLeaderboard"),
            c: zGuessTheMoveLeaderboardBlock,
        }),
        z.object({
            t: z.literal("FeaturedStreamingChannel"),
            c: zFeaturedStreamingChannelBlock,
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type BlockKind = z.infer<typeof zBlockKind>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 27
export const zLayoutBlock = z.object({
    id: z.string().uuid(),
    kind: zBlockKind,
    featureFlagId: z.nullable(z.string()).default(null),
});
export type LayoutBlock = z.infer<typeof zLayoutBlock>;

// Defined in crates/edge/src/features/layout/blocks.rs @ line 21
export const zCalculatedLayout = z.object({
    blocks: z.array(zLayoutBlock),
});
export type CalculatedLayout = z.infer<typeof zCalculatedLayout>;

// Defined in crates/protocol/src/entities/analysis.rs @ line 41
export const zArrow = z.object({
    from: zSquare,
    to: zSquare,
    color: z.string(),
});
export type Arrow = z.infer<typeof zArrow>;

// Defined in crates/protocol/src/chess/pieces.rs @ line 19
const zUniquePieceInner = z.number();
type UniquePieceInner = z.infer<typeof zUniquePieceInner>;
export type UniquePiece = Opaque<UniquePieceInner, "UniquePiece">;
export const zUniquePiece = zUniquePieceInner.transform(val => val as UniquePiece);

// Defined in crates/edge/src/realtime/messages/response/ply.rs @ line 30
export const zPlyKind = z.discriminatedUnion("t", [
    z.object({
        t: z.literal("normal"),
        c: z.object({
            piece: zUniquePiece,
            from: zSquare,
            to: zSquare,
            capturedPiece: z.nullable(zUniquePiece).default(null),
            promotion: z.nullable(zRole).default(null),
        }),
    }),
    z.object({
        t: z.literal("enPassant"),
        c: z.object({
            piece: zUniquePiece,
            from: zSquare,
            to: zSquare,
            capturedPiece: zUniquePiece,
            capturedSquare: zSquare,
        }),
    }),
    z.object({
        t: z.literal("castle"),
        c: z.object({
            kingPiece: zUniquePiece,
            rookPiece: zUniquePiece,
            kingFrom: zSquare,
            kingTo: zSquare,
            rookFrom: zSquare,
            rookTo: zSquare,
        }),
    }),
]);
export type PlyKind = z.infer<typeof zPlyKind>;

// Defined in crates/edge/src/realtime/messages/response/evaluation.rs @ line 10
export const zRealtimeEvaluation = z.object({
    plyId: zPlyId,
    depth: z.number(),
    pvNumber: z.number(),
    pvLine: z.array(zPlyKind),
    scalar: zScalarScore,
    winningChances: z.nullable(zWinningChances).default(null),
});
export type RealtimeEvaluation = z.infer<typeof zRealtimeEvaluation>;

// Defined in crates/edge/src/realtime/messages/response/ply.rs @ line 13
export const zPly = z.object({
    id: zPlyId,
    san: z.string(),
    humanMovetext: z.string(),
    color: zSerializableColor,
    timestamp: z.string().transform(arg => new Date(arg)),
    clkSeconds: z.nullable(z.number()).default(null),
    emtSeconds: z.nullable(z.number()).default(null),
    kind: zPlyKind,
    deletedAt: z.nullable(z.string().transform(arg => new Date(arg))).default(null),
    plyIndex: z.number(),
});
export type Ply = z.infer<typeof zPly>;

// Defined in crates/edge/src/realtime/messages/response/game/game_ply_data.rs @ line 17
export const zGamePlyData = z.object({
    gameId: zGameId,
    startingPosition: zPieces,
    startingPositionFen: z.string(),
    plies: z.array(zPly),
});
export type GamePlyData = z.infer<typeof zGamePlyData>;

// Defined in crates/edge/src/realtime/messages/response/analysis.rs @ line 23
export const zAnnotatedPly = z.object({
    ply: zPly,
    arrows: z.array(zArrow),
    highlighted_squares: z.record(zSquare, zSquareHighlightColor),
    piece_emojis: z.record(zSquare, z.string()),
});
export type AnnotatedPly = z.infer<typeof zAnnotatedPly>;

// Defined in crates/edge/src/realtime/messages/response/analysis.rs @ line 14
export const zAnalysis = z.object({
    id: z.string().uuid(),
    startingPosition: zPieces,
    annotatedPlies: z.array(zAnnotatedPly),
    startingPositionFen: z.string(),
});
export type Analysis = z.infer<typeof zAnalysis>;

// Defined in crates/edge/src/realtime/messages/response/feed.rs @ line 15
export const zFlatFeedRecord = z.object({
    id: z.string().uuid(),
    parentId: z.nullable(z.string().uuid()).default(null),
    createdAt: z.string().transform(arg => new Date(arg)),
    updatedAt: z.string().transform(arg => new Date(arg)),
    timestamp: z.string().transform(arg => new Date(arg)),
    content: z.nullable(z.any()).default(null),
    highlight: z.boolean(),
    imageId: z.nullable(z.string().uuid()).default(null),
    iconImageId: z.nullable(z.string().uuid()).default(null),
    bgColor: z.nullable(z.string()).default(null),
    textColor: z.nullable(z.string()).default(null),
    twitterUrl: z.nullable(z.string()).default(null),
    youtubeUrl: z.nullable(z.string()).default(null),
    draft: z.boolean(),
    ply: z.nullable(zPly).default(null),
    analysis: z.nullable(zAnalysis).default(null),
    poll: z.nullable(zPoll).default(null),
    commentator: z.nullable(zFeedRecordCommentator).default(null),
    video: z.nullable(zVideo).default(null),
});
export type FlatFeedRecord = z.infer<typeof zFlatFeedRecord>;

// Defined in crates/edge/src/realtime/messages/response/game/game_report_data.rs @ line 214
export const zFeedRecordUpdate = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("Updated"),
            c: z.array(zFlatFeedRecord),
        }),
        z.object({
            t: z.literal("Removed"),
            c: z.string().uuid(),
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type FeedRecordUpdate = z.infer<typeof zFeedRecordUpdate>;

// Defined in crates/edge/src/realtime/messages/response/game/game_report_data.rs @ line 20
export const zGameReportData = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("GameReportAvailability"),
            c: z.array(z.string()),
        }),
        z.object({
            t: z.literal("GameReportMetaUpdated"),
            c: zGameReportMeta,
        }),
        z.object({
            t: z.literal("FeedRecordUpdate"),
            c: zFeedRecordUpdate,
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type GameReportData = z.infer<typeof zGameReportData>;

// Defined in crates/edge/src/realtime/messages/response/response.rs @ line 38
export const zClientResponse = z
    .discriminatedUnion("t", [
        z.object({
            t: z.literal("GameMetadataUpdated"),
            c: zGameMetadata,
        }),
        z.object({
            t: z.literal("GamesMetadata"),
            c: z.array(zGameMetadata),
        }),
        z.object({
            t: z.literal("GamesMetadataV2"),
            c: zGamesMetadata,
        }),
        z.object({
            t: z.literal("GamePliesUpdated"),
            c: zGamePlyData,
        }),
        z.object({
            t: z.literal("MachineAnalysesUpdated"),
            c: zMachineAnalysisUpdate,
        }),
        z.object({
            t: z.literal("GameReportUpdated"),
            c: zGameReportData,
        }),
        z.object({
            t: z.literal("PlyEvaluationUpdated"),
            c: z.object({
                eval: zRealtimeEvaluation,
            }),
        }),
        z.object({
            t: z.literal("PlyEvaluations"),
            c: z.object({
                evals: z.array(zRealtimeEvaluation),
            }),
        }),
        z.object({
            t: z.literal("TournamentStandingsUpdated"),
            c: z.object({
                tournament_standings: zRealtimeTournamentStandings,
            }),
        }),
        z.object({
            t: z.literal("CountryStandingsUpdated"),
            c: z.object({
                country_standings: zRealtimeCountryStandings,
            }),
        }),
        z.object({
            t: z.literal("Guess"),
            c: zGuessedMoveUpdate,
        }),
        z.object({
            t: z.literal("FollowedPlayersDataUpdated"),
            c: zFollowedPlayersData,
        }),
        z.object({
            t: z.literal("FollowedPlayersDataUpdatedV2"),
            c: zFollowedPlayersDataV2,
        }),
        z.object({
            t: z.literal("TrialUpdated"),
            c: zTrial,
        }),
        z.object({
            t: z.literal("Trials"),
            c: zTrials,
        }),
        z.object({
            t: z.literal("TournamentBracketsUpdated"),
            c: zRealtimeTournamentBrackets,
        }),
        z.object({
            t: z.literal("TournamentPlayers"),
            c: zTournamentPlayers,
        }),
        z.object({
            t: z.literal("StreamingChannelSessionsUpdated"),
            c: zStreamingChannelSessionsData,
        }),
        z.object({
            t: z.literal("unknown"),
            c: z.undefined(),
            variant: z.string(),
        }),
    ])
    .catch(err => {
        if (err.error.issues[0]?.code === "invalid_union_discriminator") {
            return { t: "unknown", variant: err.input.t };
        } else {
            throw err.error;
        }
    });
export type ClientResponse = z.infer<typeof zClientResponse>;

// Defined in crates/edge/src/realtime/messages/response/response.rs @ line 78
export const zServerMessageEnvelope = z.object({
    channel: zChannel,
    message: zClientResponse,
});
export type ServerMessageEnvelope = z.infer<typeof zServerMessageEnvelope>;
